/* eslint-disable react-hooks/exhaustive-deps */
import React, {useMemo} from "react"
import {useTranslation} from "react-i18next"
import {KlassDropdown, KlassDropdownAsyncProps} from "../Select"
import {formatCodeName} from "helpers"
import {useAllPrograms, useProgramsByTerm} from "hooks"
import {intersection, isEmpty, map, orderBy, toLower} from "lodash"
import styles from "./ProgramSelect.module.css"
import {Tag} from "antd"
import cx from "classnames"

type Props = Partial<KlassDropdownAsyncProps> & {
    campusId?: number | number[]
    degreeLevelId?: number | number[]
    isActive?: boolean
    termIds?: number[]
    excludeProgramIds?: number[]
}

const ProgramSelect: React.FC<Props> = ({campusId, termIds, degreeLevelId, isActive, excludeProgramIds, ...props}) => {
    const {t} = useTranslation(["common"])
    const {programs} = useAllPrograms()
    const {programs: programsByTerm} = useProgramsByTerm({termIds})

    const options = useMemo(() => {
        if (termIds?.length) {
            return excludeProgramIds?.length
                ? programsByTerm.filter((program) => !excludeProgramIds.includes(program.id))
                : programsByTerm
        }
        return programs.filter((program) => {
            if (isActive != null) {
                if (program.isActive !== isActive) return false
            }
            if (campusId) {
                if (!intersection(program.campusIds ?? [], Array.isArray(campusId) ? campusId : [campusId]).length)
                    return false
            }
            if (degreeLevelId) {
                if (!(Array.isArray(degreeLevelId) ? degreeLevelId : [degreeLevelId]).includes(program.degreeLevelId))
                    return false
            }
            if (excludeProgramIds?.includes(program.id)) return false
            return true
        })
    }, [programs, programsByTerm, termIds, campusId, degreeLevelId, isActive, excludeProgramIds])

    const groupedPrograms = useMemo(() => {
        const _options: any[] = []
        const activePrograms = orderBy(
            options.filter((program) => program.isActive),
            ["code", "name"],
            ["asc", "asc"]
        )
        const inactivePrograms = orderBy(
            options.filter((program) => !program.isActive),
            ["code", "name"],
            ["asc", "asc"]
        )
        if (activePrograms.length) {
            _options.push({
                label: "Active",
                options: activePrograms
            })
        }
        if (inactivePrograms.length) {
            _options.push({
                label: "Inactive",
                options: inactivePrograms
            })
        }
        return _options
    }, [options])

    return (
        <KlassDropdown
            {...props}
            key={JSON.stringify({campusId, degreeLevelId, isActive, termIds, excludeProgramIds})}
            placeholder={props.placeholder || t("selectField.placeholder")}
            options={groupedPrograms}
            valueKey="id"
            labelKey="name"
            filterOption={(option, inputValue) =>
                inputValue ? toLower(formatCodeName(option.data)).includes(toLower(inputValue)) : true
            }
            formatOptionLabel={(option: any) => {
                if (option.code) {
                    const campuses = map(option.campuses, (campus) => campus.name).join(", ")
                    return (
                        <div className={styles.labelWrapper}>
                            <div className={styles.nameWrap}>
                                <Tag color="blue">{option.code}</Tag>
                                <span className={styles.name}>{option.name}</span>
                            </div>
                            <span className={cx(styles.campusInfo, {[styles.noCampuses]: isEmpty(campuses)})}>
                                {isEmpty(campuses) ? "No campuses" : campuses}
                            </span>
                        </div>
                    )
                }
                return option.name
            }}
        />
    )
}

export default ProgramSelect
